/**
 * GPS WebSocket Connection Manager
 * Handles WebSocket connection lifecycle and reconnection logic
 */

import type { GpsConnectionState } from './types';

/**
 * Connection configuration interface
 */
export interface ConnectionConfig {
  url: string;
  maxReconnectAttempts: number;
  reconnectDelay: number;
  heartbeatInterval?: number;
}

/**
 * Connection event callbacks
 */
export interface ConnectionCallbacks {
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Event) => void;
  onMessage?: (event: MessageEvent) => void;
  onStateChange?: (state: GpsConnectionState) => void;
}

/**
 * WebSocket dependency interface for testing
 */
export interface WebSocketDependency {
  new(url: string): WebSocket;
}

/**
 * Connection Manager class
 * Separated connection logic for better testability and maintainability
 */
export class ConnectionManager {
  private config: ConnectionConfig;
  private callbacks: ConnectionCallbacks = {};
  private WebSocketClass: WebSocketDependency;
  
  // Connection state
  private ws: WebSocket | null = null;
  private currentState: GpsConnectionState = 'idle';
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts: number = 0;
  private isConnecting: boolean = false;

  /**
   * Create connection manager with dependency injection
   */
  constructor(
    config: ConnectionConfig, 
    WebSocketClass: WebSocketDependency = WebSocket,
    callbacks: ConnectionCallbacks = {}
  ) {
    this.config = config;
    this.WebSocketClass = WebSocketClass;
    this.callbacks = callbacks;
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.isConnecting || this.currentState === 'connected') {
      return;
    }

    try {
      this.isConnecting = true;
      this.setState('connecting');
      
      this.ws = new this.WebSocketClass(this.config.url);
      this.setupWebSocketHandlers();
      
    } catch (error) {
      this.isConnecting = false;
      this.setState('failed');
      this.callbacks.onError?.(error as Event);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.clearReconnectTimer();
    this.reconnectAttempts = this.config.maxReconnectAttempts; // Disable reconnection
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.setState('disconnected');
    this.isConnecting = false;
  }

  /**
   * Send message through WebSocket
   */
  send(data: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }
    
    this.ws.send(data);
  }

  /**
   * Get current connection state
   */
  getState(): GpsConnectionState {
    return this.currentState;
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.currentState === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Update connection configuration
   */
  updateConfig(newConfig: Partial<ConnectionConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Update callbacks
   */
  setCallbacks(callbacks: ConnectionCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Destroy connection manager
   */
  destroy(): void {
    this.clearReconnectTimer();
    this.disconnect();
    this.callbacks = {};
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.isConnecting = false;
      this.reconnectAttempts = 0; // Reset on successful connection
      this.setState('connected');
      this.callbacks.onOpen?.();
    };

    this.ws.onclose = (event) => {
      this.isConnecting = false;
      this.setState('disconnected');
      this.callbacks.onClose?.(event);
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      this.isConnecting = false;
      this.setState('failed');
      this.callbacks.onError?.(error);
    };

    this.ws.onmessage = (event) => {
      this.callbacks.onMessage?.(event);
    };
  }

  /**
   * Schedule automatic reconnection
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.setState('failed');
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.setState('reconnecting');
    
    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000 // Max 30 seconds
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Set connection state and notify callbacks
   */
  private setState(state: GpsConnectionState): void {
    if (this.currentState !== state) {
      this.currentState = state;
      this.callbacks.onStateChange?.(state);
    }
  }
}